import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Shield,
  Briefcase,
  Server,
  Video,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  GitBranch,
  GitPullRequest,
  HardDrive,
  Webhook,
  Bot,
  Factory,
  MessagesSquare,
  Cpu,
  Plug,
  Layers,
  FolderKanban,
  Building2,
  ShieldCheck,
  CheckSquare,
  KeyRound,
  Zap,
  ListChecks,
  BookOpen,
  Mail,
  Search,
  Mic,
  AudioLines,
  KanbanSquare,
  Box,
  Store,
  UserRound,
  Share2,
  Package,
  Tag,
  ClipboardList,
  CreditCard,
  BarChart3,
  MonitorCheck,
  Film,
  LayoutTemplate,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useState } from 'react';

interface NavItem {
  to: string;
  label: string;
  icon?: LucideIcon;
  /** Route fragment(s) that mark the item active (paths that start with). */
  match?: string[];
}

interface NavGroup {
  title: string;
  icon: LucideIcon;
  items: NavItem[];
}

const topItems: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/users', label: 'Users', icon: Users },
  { to: '/services', label: 'Services', icon: Server },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const navGroups: NavGroup[] = [
  {
    title: 'Agents',
    icon: Bot,
    items: [
      { to: '/factories', label: 'Factories', icon: Factory, match: ['/factories'] },
      { to: '/conversations', label: 'Conversations', icon: MessagesSquare, match: ['/conversations'] },
      { to: '/models', label: 'Models', icon: Cpu, match: ['/models'] },
      { to: '/providers', label: 'Providers', icon: Plug, match: ['/providers'] },
      { to: '/model-collections', label: 'Collections', icon: Layers, match: ['/model-collections'] },
    ],
  },
  {
    title: 'Platform',
    icon: Building2,
    items: [
      { to: '/projects', label: 'Projects', icon: FolderKanban, match: ['/projects'] },
      { to: '/organizations', label: 'Organizations', icon: Building2, match: ['/organizations'] },
      { to: '/roles', label: 'Roles', icon: ShieldCheck, match: ['/roles'] },
      { to: '/user-groups', label: 'User Groups', icon: Users, match: ['/user-groups'] },
      { to: '/approvals', label: 'Approvals', icon: CheckSquare, match: ['/approvals'] },
      { to: '/secrets', label: 'Secrets', icon: KeyRound, match: ['/secrets'] },
      { to: '/mailboxes', label: 'Mailboxes', icon: Mail, match: ['/mailboxes'] },
      { to: '/daytona', label: 'Sandboxes', icon: Box, match: ['/daytona', '/instances'] },
      { to: '/planning', label: 'Planning', icon: KanbanSquare, match: ['/planning'] },
    ],
  },
  {
    title: 'Commerce',
    icon: Store,
    items: [
      { to: '/stores', label: 'Stores & workspaces', icon: Store, match: ['/stores', '/workspaces'] },
      { to: '/customers', label: 'Customers', icon: UserRound, match: ['/customers'] },
      { to: '/shares', label: 'Shares', icon: Share2, match: ['/shares'] },
      { to: '/catalog', label: 'Catalog', icon: Package, match: ['/catalog'] },
      { to: '/discounts', label: 'Discounts', icon: Tag, match: ['/discounts'] },
      { to: '/orders', label: 'Orders', icon: ClipboardList, match: ['/orders'] },
      { to: '/payments', label: 'Payments', icon: CreditCard, match: ['/payments'] },
      { to: '/reports', label: 'Reporting', icon: BarChart3, match: ['/reports'] },
      { to: '/pos-rooms', label: 'POS rooms', icon: MonitorCheck, match: ['/pos-rooms'] },
    ],
  },
  {
    title: 'Cinema',
    icon: Film,
    items: [
      { to: '/cinema/activities', label: 'Activities', icon: Film, match: ['/cinema/activities'] },
      { to: '/cinema/movies', label: 'Movies', icon: Film, match: ['/cinema/movies'] },
      { to: '/cinema/rooms', label: 'Rooms', icon: MonitorCheck, match: ['/cinema/rooms'] },
      { to: '/cinema/seat-maps', label: 'Seat maps', icon: MonitorCheck, match: ['/cinema/seat-maps'] },
      { to: '/cinema/showtimes', label: 'Showtimes', icon: ClipboardList, match: ['/cinema/showtimes'] },
    ],
  },
  {
    title: 'Kiosks',
    icon: LayoutTemplate,
    items: [
      { to: '/kiosks/templates', label: 'Template builder', icon: LayoutTemplate, match: ['/kiosks/templates'] },
      { to: '/kiosks/devices', label: 'Devices', icon: MonitorCheck, match: ['/kiosks/devices'] },
    ],
  },
  {
    title: 'Automation',
    icon: Zap,
    items: [
      { to: '/webhooks', label: 'Webhooks', icon: Webhook, match: ['/webhooks'] },
      { to: '/bulk-operations', label: 'Bulk Operations', icon: ListChecks, match: ['/bulk-operations'] },
      { to: '/jobs', label: 'Jobs & Queues', icon: Briefcase, match: ['/jobs'] },
    ],
  },
  {
    title: 'Knowledge',
    icon: BookOpen,
    items: [
      { to: '/repositories', label: 'Repositories', icon: GitBranch, match: ['/repositories'] },
      { to: '/repositories/pull-requests', label: 'Pull Requests', icon: GitPullRequest, match: ['/repositories/pull-requests'] },
      { to: '/indexer', label: 'Indexer', icon: Search, match: ['/indexer'] },
    ],
  },
  {
    title: 'Media',
    icon: AudioLines,
    items: [
      { to: '/voices', label: 'Voices', icon: Mic, match: ['/voices'] },
      { to: '/meetings', label: 'Meetings', icon: Video, match: ['/meetings'] },
    ],
  },
  {
    title: 'Policies',
    icon: Shield,
    items: [
      { to: '/policies', label: 'Policies', icon: Shield, match: ['/policies'] },
      { to: '/policies/builder', label: 'Policy Builder', icon: ShieldCheck, match: ['/policies/builder'] },
    ],
  },
];

/** Storage lives inside Knowledge as a collapsible submenu (existing behavior). */
const storageChildren = [
  { to: '/storage/sources', label: 'Sources' },
  { to: '/storage/collections', label: 'Collections' },
];

interface SidebarProps {
  className?: string;
}

function isItemActive(pathname: string, item: NavItem): boolean {
  const prefixes = item.match ?? [item.to];
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function isGroupActive(pathname: string, group: NavGroup): boolean {
  return group.items.some((item) => isItemActive(pathname, item));
}

export function Sidebar({ className }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  // Default-expand the group that holds the current route.
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const group of navGroups) {
      initial[group.title] = isGroupActive(location.pathname, group);
    }
    return initial;
  });
  const [storageOpen, setStorageOpen] = useState(
    location.pathname.startsWith('/storage/'),
  );

  const toggleGroup = (title: string) =>
    setOpenGroups((prev) => ({ ...prev, [title]: !prev[title] }));

  const itemClasses = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
      isActive
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
    );

  const isStorageActive =
    location.pathname === '/storage' || location.pathname.startsWith('/storage/');
  const storageExpanded = storageOpen || isStorageActive;

  return (
    <aside
      className={cn(
        'flex flex-col border-r bg-card transition-all duration-300',
        collapsed ? 'w-16' : 'w-64',
        className,
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center gap-2 border-b px-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">
          C
        </div>
        {!collapsed && (
          <span className="text-lg font-semibold tracking-tight">Cluster</span>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-2">
        <nav className="flex flex-col gap-1 px-2">
          {topItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={(args) =>
                cn(itemClasses(args), collapsed && 'justify-center px-2')
              }
            >
              {item.icon && <item.icon className="h-4 w-4 shrink-0" />}
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}

          {/* Grouped navigation */}
          {navGroups.map((group) => {
            const groupActive = isGroupActive(location.pathname, group);
            const expanded = openGroups[group.title];
            return (
              <div key={group.title} className={cn('mt-1', collapsed && 'mt-2')}>
                <button
                  type="button"
                  onClick={() => !collapsed && toggleGroup(group.title)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    groupActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    collapsed && 'justify-center px-2',
                  )}
                  title={collapsed ? group.title : undefined}
                >
                  <group.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && (
                    <span className="flex-1 text-left font-semibold">{group.title}</span>
                  )}
                  {!collapsed &&
                    (expanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 opacity-70" />
                    ))}
                </button>

                {!collapsed && expanded && (
                  <div className="mt-1 flex flex-col gap-1 pl-9">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={itemClasses}
                      >
                        <span>{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Storage submenu — sits inside Knowledge group */}
          <div className="mt-1">
            <button
              type="button"
              onClick={() => !collapsed && setStorageOpen(!storageOpen)}
              className={cn(
                'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isStorageActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                collapsed && 'justify-center px-2',
              )}
              title={collapsed ? 'Storage' : undefined}
            >
              <HardDrive className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="flex-1 text-left font-semibold">Storage</span>}
              {!collapsed &&
                (storageExpanded ? (
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 opacity-70" />
                ))}
            </button>

            {!collapsed && storageExpanded && (
              <div className="mt-1 flex flex-col gap-1 pl-9">
                {storageChildren.map((child) => (
                  <NavLink
                    key={child.to}
                    to={child.to}
                    className={itemClasses}
                  >
                    <span>{child.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </nav>
      </ScrollArea>

      {/* Collapse toggle */}
      <Separator />
      <div className="p-2">
        <Button
          variant="ghost"
          size="icon"
          className="w-full"
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>
    </aside>
  );
}
